import { Injectable } from '@angular/core';
import { Names } from '@mode-switch/model';
import { BasicChannel, EncChannel, Encoding } from '@mode-vegas/vegas';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledFormula } from '@mode/shared/util-switch';
import { FieldDropHandlerService } from '../cartesian-builder-common/field-drop-handler.service';

@Injectable({
  providedIn: 'root',
})
/**
 * This service implementation handles the specifics of the grouped bar chart.
 * Unlike the stacked bar which uses the same encoding for HighCharts and Muze,
 * the encoding for grouped bar is different.
 * HighCharts uses a faceted approach with Measure Names on X and the X-axis on
 * Columns. Muze uses a more direct approach with the X-axis field on X and a
 * 'transform' property to group the bars.
 * When handling drag-drop, we need to make different encoding updates based on
 * whether the renderer is HighCharts or Muze.
 */
export class GroupedDropHandlerService extends FieldDropHandlerService {
  handleDropOnEmpty(
    view: FlamingoTypes.ModeView | undefined,
    encoding: Encoding,
    chartType: ChartTypes.ChartType,
    formula: CompiledFormula,
    channel: BasicChannel,
    source: EncChannel | 'field-list' | undefined,
    dropIndex: number | undefined,
    useMuzeRenderer: boolean,
    zoneType: EncodingHelpers.ZoneType
  ) {
    const hasColor = this.hasColorColumn(encoding);
    const secondaryAxisFields = this.getSecondaryAxisFields(encoding, chartType);

    if ((channel === 'column' || channel === 'row') && secondaryAxisFields[0] !== undefined) {
      // For Muze encodings, the X-Axis is stored on x[0] (ie. secondaryAxisFields[0])
      // Replace that field with the new formula. If there is nothing on X, we would fall through
      // and just add the new field.
      // For HighCharts encodings, the X-Axis is always Measure Names. So, we either replace
      // the field in column[0] or add it.
      return useMuzeRenderer
        ? this.replaceFieldWithFormula(encoding, secondaryAxisFields[0], formula, channel, source, zoneType)
        : this.addOrReplaceHighChartsXAxis(view, encoding, chartType, formula, channel, source, zoneType);
    } else if (channel === 'value' && hasColor && encoding.value[0] !== undefined) {
      return this.replaceFieldWithFormula(
        encoding,
        encoding.value[0],
        formula,
        channel,
        source,
        EncodingHelpers.ZoneType.Aggregate
      );
    } else {
      return this.addFormula(view, encoding, formula, channel, dropIndex, zoneType);
    }
  }

  getSecondaryFields(encoding: Encoding, chartType: ChartTypes.ChartType) {
    const isHorizontalChart = EncodingHelpers.HorizontalChartTypes.has(chartType);

    const secondaryDiscrete = isHorizontalChart ? encoding.row : encoding.column;
    const secondaryAxisFields = this.getSecondaryAxisFields(encoding, chartType);
    const fields = [...secondaryDiscrete, ...secondaryAxisFields];
    const nonNamesField = fields.find((f) => f.formula !== Names.Key);
    return nonNamesField != null ? [nonNamesField] : [];
  }

  getSecondaryAxisFields(encoding: Encoding, chartType: ChartTypes.ChartType) {
    const isHorizontalChart = EncodingHelpers.HorizontalChartTypes.has(chartType);

    return isHorizontalChart
      ? encoding.y[0] != null
        ? [encoding.y[0]]
        : []
      : encoding.x[0] != null
        ? [encoding.x[0]]
        : [];
  }

  addOrReplaceHighChartsXAxis(
    view: FlamingoTypes.ModeView | undefined,
    encoding: Encoding,
    chartType: ChartTypes.ChartType,
    formula: CompiledFormula,
    channel: BasicChannel,
    source: EncChannel | 'field-list' | undefined,
    zoneType: EncodingHelpers.ZoneType
  ) {
    // For HighCharts, the encoding has:
    //  - Measure Names on x[0]
    //  - X-Axis field (or none) on column[0]
    //
    // Look for the secondary field (column[0])
    //  - If there is one, then replace.
    //  - Otherwise, add the field to columns
    //
    const secondaryFields = this.getSecondaryFields(encoding, chartType);
    if (secondaryFields.length > 0) {
      return this.replaceFieldWithFormula(encoding, secondaryFields[0], formula, channel, source, zoneType);
    }
    return this.addFormula(view, encoding, formula, channel, 0, zoneType);
  }
}
